import type { ReactNode } from "react";

import { useSelfUpdate } from "../../api/queries";
import type { UserPreferences } from "../../api/types";
import { useAuth } from "../../auth/auth-context";
import { useTheme } from "../../theme/theme-context";
import type { ThemeMode } from "../../theme/theme-context";
import { Select } from "../../ui/Select";
import { SettingsPage, SettingsSection } from "../../ui/SettingsSection";
import { Toggle } from "../../ui/Toggle";
import { useToast } from "../../ui/toast-context";

/**
 * One language ships today, but the control is data-driven so adding a
 * translation is a line here rather than a new control. A value the deployment
 * does not ship is a `validation_error`, so the list is the deployment's list.
 */
export const languages = [{ value: "en_US", label: "English (US)" }];

const themeOptions: { value: ThemeMode; label: string }[] = [
  { value: "system", label: "Match my system" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

const notificationBadgeOptions = [
  { value: "count" as const, label: "A count of unread items" },
  { value: "indicator" as const, label: "A dot, with no number" },
  { value: "disabled" as const, label: "Nothing" },
];

const FALLBACK_ZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/New_York",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Singapore",
  "Australia/Sydney",
];

/** `Intl.supportedValuesOf` is recent; fall back to a short list where it is missing. */
export function timezoneNames(): string[] {
  const intl = Intl as { supportedValuesOf?: (key: string) => string[] };
  const supported =
    typeof intl.supportedValuesOf === "function" ? intl.supportedValuesOf("timeZone") : undefined;
  const zones = supported !== undefined && supported.length > 0 ? supported : FALLBACK_ZONES;
  // `supportedValuesOf` omits plain UTC; people ask for it by that name.
  return zones.includes("UTC") ? zones : ["UTC", ...zones];
}

interface PreferenceToggle {
  key: keyof UserPreferences;
  label: string;
  description?: string;
  defaultOn: boolean;
}

const editingToggles: PreferenceToggle[] = [
  {
    key: "seamlessEdit",
    label: "Edit without switching modes",
    description: "Type straight into a document rather than entering an edit mode first.",
    defaultOn: true,
  },
  {
    key: "fullWidthDocuments",
    label: "New documents are full width",
    defaultOn: false,
  },
  {
    key: "enableSmartText",
    label: "Smart text replacements",
    description: "Curly quotes, em dashes and ellipses as you type.",
    defaultOn: true,
  },
  {
    key: "codeBlockLineNumbers",
    label: "Line numbers in code blocks",
    defaultOn: true,
  },
  {
    key: "showDocumentStats",
    label: "Show word count and reading time",
    defaultOn: false,
  },
];

const behaviourToggles: PreferenceToggle[] = [
  {
    key: "rememberLastPath",
    label: "Return me to where I left off",
    description: "Open the last document you had open when you sign in again.",
    defaultOn: true,
  },
  {
    key: "useCursorPointer",
    label: "Pointer cursor on clickable things",
    defaultOn: true,
  },
];

const commentToggles: PreferenceToggle[] = [
  {
    key: "sortCommentsByOrderInDocument",
    label: "Order comments by position in the document",
    description: "Otherwise they are ordered by when they were written.",
    defaultOn: true,
  },
  {
    key: "commentsInGutter",
    label: "Show comment markers in the gutter",
    defaultOn: true,
  },
];

export function PreferencesScreen(): ReactNode {
  const { session } = useAuth();
  const user = session?.info.user;
  const preferences = user?.preferences ?? {};
  const theme = useTheme();
  const update = useSelfUpdate();
  const toast = useToast();

  const onError = (error: Error): void => {
    toast.show(error.message, "error");
  };

  const setPreference = (patch: UserPreferences): void => {
    update.mutate({ preferences: patch }, { onError });
  };

  // `seamlessEdit` is the one preference whose default is the workspace's choice
  // rather than a constant (`docs/spec/users-and-teams.md` §2).
  const defaultFor = (toggle: PreferenceToggle): boolean =>
    toggle.key === "seamlessEdit"
      ? (session?.info.team.preferences?.seamlessEdit ?? toggle.defaultOn)
      : toggle.defaultOn;

  const renderToggles = (toggles: PreferenceToggle[]): ReactNode =>
    toggles.map((toggle) => (
      <Toggle
        key={toggle.key}
        label={toggle.label}
        {...(toggle.description === undefined ? {} : { description: toggle.description })}
        checked={(preferences[toggle.key] as boolean | undefined) ?? defaultFor(toggle)}
        onChange={(checked) => {
          setPreference({ [toggle.key]: checked });
        }}
      />
    ));

  const zones = timezoneNames();
  const currentZone = user?.timezone ?? null;
  const zoneOptions = (
    currentZone !== null && !zones.includes(currentZone) ? [currentZone, ...zones] : zones
  ).map((zone) => ({ value: zone, label: zone.replaceAll("_", " ") }));

  return (
    <SettingsPage
      title="Preferences"
      description="How the application looks and behaves for you. Nobody else is affected."
    >
      <SettingsSection title="Display">
        <div className="flex flex-col gap-4">
          <Select
            label="Language"
            value={user?.language ?? languages[0]?.value ?? "en_US"}
            options={languages}
            onChange={(value) => {
              update.mutate({ language: value }, { onError });
            }}
          />
          <Select
            label="Colour theme"
            description="Stored in this browser, not on your account."
            value={theme.mode}
            options={themeOptions}
            onChange={(value) => {
              theme.setMode(value);
            }}
          />
          <Select
            label="Time zone"
            description="Used for dates and times shown to you."
            value={currentZone ?? zoneOptions[0]?.value ?? "UTC"}
            options={zoneOptions}
            onChange={(value) => {
              update.mutate({ timezone: value }, { onError });
            }}
          />
          <Select
            label="Notification badge"
            value={preferences.notificationBadge ?? "count"}
            options={notificationBadgeOptions}
            onChange={(value) => {
              setPreference({ notificationBadge: value });
            }}
          />
        </div>
      </SettingsSection>

      <SettingsSection title="Editing">{renderToggles(editingToggles)}</SettingsSection>
      <SettingsSection title="Comments">{renderToggles(commentToggles)}</SettingsSection>
      <SettingsSection title="Behaviour">{renderToggles(behaviourToggles)}</SettingsSection>
    </SettingsPage>
  );
}
